import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from './client';
import { API } from './endpoints';
import { adjustmentKeys } from './adjustmentApi';
import { mealPatternKeys } from './mealPatternApi';

/**
 * Single change-history snapshot row — matches the C# DTO in
 * `DietechModels/Models/ChangeHistoryRecord.cs`. Every name/value
 * field is sourced from a DENORMALIZED column on the log row so the
 * record reflects values AS THEY WERE at the edit time (no joins to
 * current lookups). Some fields are populated only on the meal-
 * pattern side, others only on the adjustment side; the union is
 * modeled so a single dialog can render both.
 */
export interface ChangeHistoryRecord {
  // Identity / flags
  logPKey: number;
  isOriginal: boolean;
  wasDeleted: boolean;
  isInactive: boolean;

  // Production-row FKs captured on the log row — used to pivot
  // the Deleted Items dialog into row-history mode for the
  // original row. One of mealPattern* is set on an MP log row;
  // one of adjustment* on an adjustment log row.
  fKeyPrdMealPattern: number | null;
  fKeyPrdMealPatternGlobal: number | null;
  fKeyPrdAdjustment: number | null;
  fKeyPrdAdjustmentGlobal: number | null;

  // Transaction metadata (from tblLog_Transaction +
  // tblLog_Transaction_Type)
  transactionType: string;
  dateTimeOfLog: string | null;
  userName: string;
  userFullName: string;
  method: string;

  // Snapshot values — common across MP + Adjustment
  itemName: string;
  menuCategory: string;
  consistName: string;
  portionName: string;
  serviceItemName: string;
  foodGroupName: string;
  itemTypeName: string;
  itemSize: string;          // pre-formatted "0.5 oz"
  globalCommonName: string;
  globalGroupName: string;

  // Meal-pattern-only
  mealtime: number | null;
  specialDevicesName: string;
  note: string;
  addToMenu: boolean;
  day1: boolean;
  day2: boolean;
  day3: boolean;
  day4: boolean;
  day5: boolean;
  day6: boolean;
  day7: boolean;
  medPass: boolean;
  chargeable: boolean;
  chargeableLabel: boolean;
  nourishmentLabel: boolean;
  addWhenNoSimilarItemFound: boolean;
  checkEntireMealtime: boolean;
  specialRequest: boolean;
  doNotExcludeMenuCategories: boolean;
  startDate: string | null;
  endDate: string | null;
  menuDays: string;
  itemLocked: boolean;
  itmSizeLocked: boolean;
  consistLocked: boolean;
  itemTypeLocked: boolean;
  serviceItemLocked: boolean;
  childItemType: number | null;

  // Adjustment-only
  reason: number | null;
  type: number | null;
  actionType: number | null;
  allergyGroupName: string;
  itemAdjName: string;
  consistAdjName: string;
  portionAdjName: string;
  itemSizeAdj: string;
  // 21-bit mealtime/day-of-week active grid
  activeB1: boolean; activeL1: boolean; activeD1: boolean;
  activeB2: boolean; activeL2: boolean; activeD2: boolean;
  activeB3: boolean; activeL3: boolean; activeD3: boolean;
  activeB4: boolean; activeL4: boolean; activeD4: boolean;
  activeB5: boolean; activeL5: boolean; activeD5: boolean;
  activeB6: boolean; activeL6: boolean; activeD6: boolean;
  activeB7: boolean; activeL7: boolean; activeD7: boolean;
  // Extra nourishment-slot active flags (Early AM / AM / PM / HS /
  // PM2 / HS2). Not day-specific — one flag per snapshot per slot.
  // Sourced from tblLog_Prd_Adjustment.Prd_Adjustment_Active_{slot}.
  activeEAM: boolean;
  activeAM: boolean;
  activePM: boolean;
  activeHS: boolean;
  activePM2: boolean;
  activeHS2: boolean;
  adjustmentNotes: string;
  multipleIndex: number | null;
  adjItemLocked: boolean;
  adjItmSizeLocked: boolean;
  adjConsistLocked: boolean;
  adjItemTypeLocked: boolean;
  adjServiceItemLocked: boolean;
  adjItemAdjLocked: boolean;
  ignoreMenuCategorySkips: boolean;
  ignoreDietRestrictions: boolean;
  adjMenuDays: string;
  adjChildItemType: number | null;
}

export const historyKeys = {
  all: ['history'] as const,
  // `isGlobal` is part of the cache key so legacy and global-instance
  // rows with the same PKey value don't collide.
  mpItem: (facilityId: number, pKey: number, isGlobal: boolean) =>
    [...historyKeys.all, 'mpItem', facilityId, pKey, isGlobal] as const,
  adjItem: (facilityId: number, pKey: number, isGlobal: boolean) =>
    [...historyKeys.all, 'adjItem', facilityId, pKey, isGlobal] as const,
  mpDeleted: (facilityId: number, patientId: number) =>
    [...historyKeys.all, 'mpDeleted', facilityId, patientId] as const,
  adjDeleted: (facilityId: number, patientId: number) =>
    [...historyKeys.all, 'adjDeleted', facilityId, patientId] as const,
};

export function useMPItemHistory(facilityId: number, pKey: number, isGlobal: boolean = false) {
  return useQuery({
    queryKey: historyKeys.mpItem(facilityId, pKey, isGlobal),
    queryFn: async () => {
      const { data } = await apiClient.get<ChangeHistoryRecord[]>(
        API.mpHistory(facilityId, pKey, isGlobal),
      );
      return data ?? [];
    },
    enabled: facilityId > 0 && pKey > 0,
  });
}

export function useAdjItemHistory(facilityId: number, pKey: number, isGlobal: boolean = false) {
  return useQuery({
    queryKey: historyKeys.adjItem(facilityId, pKey, isGlobal),
    queryFn: async () => {
      const { data } = await apiClient.get<ChangeHistoryRecord[]>(
        API.adjHistory(facilityId, pKey, isGlobal),
      );
      return data ?? [];
    },
    enabled: facilityId > 0 && pKey > 0,
  });
}

export function useMPDeletedHistory(facilityId: number, patientId: number) {
  return useQuery({
    queryKey: historyKeys.mpDeleted(facilityId, patientId),
    queryFn: async () => {
      const { data } = await apiClient.get<ChangeHistoryRecord[]>(
        API.mpDeletedHistory(facilityId, patientId),
      );
      return data ?? [];
    },
    enabled: facilityId > 0 && patientId > 0,
  });
}

// ─── Child-table snapshot shapes (master-detail for the
//     History → Open detail dialogs) ────────────────────────────

export interface LogMealPatternChildRecord {
  pKey: number;
  fKeyLogPrdMealPattern: number;
  itemName: string;
  itemSize: string;
  portionName: string;
  serviceItemName: string;
  consistName: string;
  foodGroupName: string;
  itemTypeName: string;
  inactive: boolean;
  medPass: boolean;
  chargeable: boolean;
  chargeableLabel: boolean;
  nourishmentLabel: boolean;
  addWhenNoSimilarItemFound: boolean;
  isOriginal: boolean;
  wasDeleted: boolean;
}

export interface LogAdjustmentChildRecord {
  pKey: number;
  fKeyLogPrdAdjustment: number;
  itemName: string;
  itemSize: string;
  consistName: string;
  serviceItemName: string;
  portionName: string;
  priority: number | null;
  isOriginal: boolean;
  wasDeleted: boolean;
}

export interface LogAdjustmentDFRecord {
  pKey: number;
  fKeyLogPrdAdjustment: number;
  dietTypeName: string;
  consistName: string;
  priority: number | null;
  isOriginal: boolean;
  wasDeleted: boolean;
}

export interface LogAdjustmentExceptionRecord {
  pKey: number;
  fKeyLogPrdAdjustment: number;
  itemName: string;
  foodGroupName: string;
  menuCategoryName: string;
  type: number | null;
  inactive: boolean;
  isOriginal: boolean;
  wasDeleted: boolean;
}

export interface LogAdjustmentOnMenuRecord {
  pKey: number;
  fKeyLogPrdAdjustment: number;
  itemName: string;
  foodGroupName: string;
  menuCategoryName: string;
  type: number | null;
  inactive: boolean;
  isOriginal: boolean;
  wasDeleted: boolean;
}

// ─── Child snapshot hooks ────────────────────────────────────
// All take a `logPKey` (the tblLog_Prd_*.PKey of the parent log
// row). React Query caches each by (facility, logPKey) so the
// detail dialog can mount + dismount freely without refetching.

export const historyChildKeys = {
  all: ['historyChild'] as const,
  mpChild: (facilityId: number, logPKey: number) =>
    [...historyChildKeys.all, 'mpChild', facilityId, logPKey] as const,
  adjChild: (facilityId: number, logPKey: number) =>
    [...historyChildKeys.all, 'adjChild', facilityId, logPKey] as const,
  adjDF: (facilityId: number, logPKey: number) =>
    [...historyChildKeys.all, 'adjDF', facilityId, logPKey] as const,
  adjException: (facilityId: number, logPKey: number) =>
    [...historyChildKeys.all, 'adjException', facilityId, logPKey] as const,
  adjOnMenu: (facilityId: number, logPKey: number) =>
    [...historyChildKeys.all, 'adjOnMenu', facilityId, logPKey] as const,
};

export function useMPChildHistory(facilityId: number, logPKey: number, enabled = true) {
  return useQuery({
    queryKey: historyChildKeys.mpChild(facilityId, logPKey),
    queryFn: async () => {
      const { data } = await apiClient.get<LogMealPatternChildRecord[]>(API.mpChildHistory(facilityId, logPKey));
      return data ?? [];
    },
    enabled: enabled && facilityId > 0 && logPKey > 0,
  });
}

export function useAdjChildHistory(facilityId: number, logPKey: number, enabled = true) {
  return useQuery({
    queryKey: historyChildKeys.adjChild(facilityId, logPKey),
    queryFn: async () => {
      const { data } = await apiClient.get<LogAdjustmentChildRecord[]>(API.adjChildHistory(facilityId, logPKey));
      return data ?? [];
    },
    enabled: enabled && facilityId > 0 && logPKey > 0,
  });
}

export function useAdjDFHistory(facilityId: number, logPKey: number, enabled = true) {
  return useQuery({
    queryKey: historyChildKeys.adjDF(facilityId, logPKey),
    queryFn: async () => {
      const { data } = await apiClient.get<LogAdjustmentDFRecord[]>(API.adjDFHistory(facilityId, logPKey));
      return data ?? [];
    },
    enabled: enabled && facilityId > 0 && logPKey > 0,
  });
}

export function useAdjExceptionHistory(facilityId: number, logPKey: number, enabled = true) {
  return useQuery({
    queryKey: historyChildKeys.adjException(facilityId, logPKey),
    queryFn: async () => {
      const { data } = await apiClient.get<LogAdjustmentExceptionRecord[]>(API.adjExceptionHistory(facilityId, logPKey));
      return data ?? [];
    },
    enabled: enabled && facilityId > 0 && logPKey > 0,
  });
}

// ─── Restore (Deleted Items → Restore button) ────────────────

export interface RestoreResult {
  success: boolean;
  newPKey: number;
  isGlobal: boolean;
  brokenFields: string[];
  error: string | null;
}

/**
 * Restore a deleted meal pattern row from its log snapshot. On
 * success returns the new production PKey + whether it was
 * restored as a global-instance row (lineage intact) or as a
 * legacy patient-owned row (lineage broken or originally legacy).
 *
 * On failure due to broken FKs, `success = false` and
 * `brokenFields` lists the missing lookup-table values (e.g.
 * "Menu Category", "Child Item"). The caller surfaces this as
 * "You cannot restore the meal pattern item. The {Field} has
 * been deleted and is no longer in the system." — same message
 * shape the WinForms history form uses.
 *
 * Invalidates the deleted-MP cache + the patient's meal pattern
 * list so the grid reflects the restored row immediately.
 */
export function useRestoreMPMutation(facilityId: number, patientId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (logPKey: number) => {
      const { data } = await apiClient.post<RestoreResult>(
        API.mpRestore(facilityId, patientId, logPKey),
      );
      return data;
    },
    onSuccess: (result) => {
      if (!result.success) return;
      queryClient.invalidateQueries({ queryKey: historyKeys.mpDeleted(facilityId, patientId) });
      // Invalidate the whole `mealPattern` prefix so every cached
      // query under it (byPatient, children, global lists, etc.)
      // refetches — needed because Restore can re-create rows
      // across several of those caches in one go.
      queryClient.invalidateQueries({ queryKey: mealPatternKeys.all });
    },
  });
}

/**
 * Restore a deleted adjustment row from its log snapshot. Same
 * shape as useRestoreMPMutation but invalidates the adjustment
 * caches. Returns the same RestoreResult shape — server returns
 * BrokenFields when FK pre-flight fails so the UI can surface
 * "X has been deleted" messages.
 */
export function useRestoreAdjMutation(facilityId: number, patientId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (logPKey: number) => {
      const { data } = await apiClient.post<RestoreResult>(
        API.adjRestore(facilityId, patientId, logPKey),
      );
      return data;
    },
    onSuccess: (result) => {
      if (!result.success) return;
      queryClient.invalidateQueries({ queryKey: historyKeys.adjDeleted(facilityId, patientId) });
      // Restore re-creates rows across the parent list AND every
      // master-detail sub-grid (Children / DF / Exceptions / OnMenu
      // / MenuDays). Invalidating the broad `adjustment` prefix
      // refetches all of them in one go so the grid + each
      // expanded master-detail panel reflect the restored row
      // without a manual refresh.
      queryClient.invalidateQueries({ queryKey: adjustmentKeys.all });
      // A restored Never Serve / Replace brings back its Meal Pattern warning.
      queryClient.invalidateQueries({ queryKey: mealPatternKeys.byPatient(facilityId, patientId) });
    },
  });
}

export function useAdjOnMenuHistory(facilityId: number, logPKey: number, enabled = true) {
  return useQuery({
    queryKey: historyChildKeys.adjOnMenu(facilityId, logPKey),
    queryFn: async () => {
      const { data } = await apiClient.get<LogAdjustmentOnMenuRecord[]>(API.adjOnMenuHistory(facilityId, logPKey));
      return data ?? [];
    },
    enabled: enabled && facilityId > 0 && logPKey > 0,
  });
}

export function useAdjDeletedHistory(facilityId: number, patientId: number) {
  return useQuery({
    queryKey: historyKeys.adjDeleted(facilityId, patientId),
    queryFn: async () => {
      const { data } = await apiClient.get<ChangeHistoryRecord[]>(
        API.adjDeletedHistory(facilityId, patientId),
      );
      return data ?? [];
    },
    enabled: facilityId > 0 && patientId > 0,
  });
}
